import { useEffect, useRef, RefObject } from 'react'
import { AppServices } from 'middleware'
import type { EventType, LogContext } from 'http/loggingService'

const DWELL_CAP_SECONDS = 4 * 3600

export type TrackFn = (
    type: EventType,
    data?: Record<string, unknown>,
    opts?: { keepalive?: boolean; context?: LogContext },
) => void

export function makeTracker(surface: string): TrackFn {
    return (type, data = {}, opts) => {
        AppServices.loggingService.logAction(type, { surface, ...data }, opts).catch(() => undefined)
    }
}

export function useDwellTracking(track: TrackFn, event: EventType, resetKey?: string | null): void {
    const trackRef = useRef(track)
    trackRef.current = track

    useEffect(() => {
        // The flush runs on unmount or pagehide, when the SPA may already show
        // the next page (or partner) — the event must carry the context from
        // when the dwell started, not from where it flushed.
        let visibleSince: number | null = null
        let context: LogContext | null = null

        const startChunk = () => {
            visibleSince = Date.now()
            context = AppServices.loggingService.captureContext()
        }
        if (document.visibilityState === 'visible') startChunk()

        const flush = () => {
            if (visibleSince == null || context == null) return
            const seconds = Math.round((Date.now() - visibleSince) / 1000)
            const chunkContext = context
            visibleSince = null
            context = null
            if (seconds < 1) return
            trackRef.current(
                event,
                { seconds: Math.min(seconds, DWELL_CAP_SECONDS), capped: seconds > DWELL_CAP_SECONDS },
                { keepalive: true, context: chunkContext },
            )
        }

        const onVisibilityChange = () => {
            if (document.visibilityState === 'hidden') flush()
            else if (visibleSince == null) startChunk()
        }

        document.addEventListener('visibilitychange', onVisibilityChange)
        window.addEventListener('pagehide', flush)
        return () => {
            flush()
            document.removeEventListener('visibilitychange', onVisibilityChange)
            window.removeEventListener('pagehide', flush)
        }
    }, [event, resetKey])
}

interface ViewedOnceOptions {
    /** continuous seconds the element must stay in view; 0 fires on first sight */
    seconds: number
    /** share of the element that must be visible — keep low for sections taller than the viewport */
    threshold?: number
    onSeen: () => void
}

export function useViewedOnce<T extends HTMLElement>({ seconds, threshold = 0.5, onSeen }: ViewedOnceOptions): RefObject<T> {
    const ref = useRef<T>(null)
    const seenRef = useRef(false)
    const onSeenRef = useRef(onSeen)
    onSeenRef.current = onSeen

    useEffect(() => {
        const el = ref.current
        if (!el || seenRef.current || typeof IntersectionObserver === 'undefined') return

        let timer: number | null = null
        let intersecting = false

        const disarm = () => {
            if (timer != null) {
                window.clearTimeout(timer)
                timer = null
            }
        }
        const fire = () => {
            if (seenRef.current) return
            seenRef.current = true
            cleanup()
            onSeenRef.current()
        }
        const arm = () => {
            if (seenRef.current || timer != null) return
            if (!intersecting || document.visibilityState !== 'visible') return
            if (seconds <= 0) {
                fire()
                return
            }
            timer = window.setTimeout(fire, seconds * 1000)
        }
        const observer = new IntersectionObserver(
            (entries) => {
                intersecting = entries[entries.length - 1]?.isIntersecting ?? false
                if (intersecting) arm()
                else disarm()
            },
            { threshold },
        )
        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') arm()
            else disarm()
        }
        function cleanup() {
            disarm()
            observer.disconnect()
            document.removeEventListener('visibilitychange', onVisibilityChange)
        }

        observer.observe(el)
        document.addEventListener('visibilitychange', onVisibilityChange)
        return cleanup
    }, [seconds, threshold])

    return ref
}

export function useScrolledIntoViewOnce<T extends HTMLElement>({ onSeen }: { onSeen: () => void }): RefObject<T> {
    const ref = useRef<T>(null)
    const seenRef = useRef(false)
    const onSeenRef = useRef(onSeen)
    onSeenRef.current = onSeen

    useEffect(() => {
        if (seenRef.current) return
        const onScroll = () => {
            const el = ref.current
            if (!el || seenRef.current || document.visibilityState !== 'visible') return
            const rect = el.getBoundingClientRect()
            if (rect.top < window.innerHeight * 0.85 && rect.bottom > 0) {
                seenRef.current = true
                window.removeEventListener('scroll', onScroll, true)
                onSeenRef.current()
            }
        }
        window.addEventListener('scroll', onScroll, { capture: true, passive: true })
        return () => window.removeEventListener('scroll', onScroll, true)
    }, [])

    return ref
}
